import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import MentionText from "@/components/MentionText";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AtSign, ArrowRight } from "lucide-react";

/**
 * /mentions — chronological list of every @mention of the viewer.
 * Tapping a row jumps to the exact message in the room.
 */
export default function Mentions() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get("/me/mentions?limit=50")
      .then(({ data }) => {
        if (!alive) return;
        setItems(data.mentions || []);
        setHasMore(!!data.has_more);
      })
      .catch((e) => toast.error(formatErr(e)))
      .finally(() => { if (alive) setLoading(false); });
    // Mark all mentions read once viewed. Fire-and-forget.
    api.post("/me/mentions/read").then(() => {
      window.dispatchEvent(new CustomEvent("nw:unread-mentions", { detail: { count: 0 } }));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const loadMore = async () => {
    if (loadingMore || !hasMore || items.length === 0) return;
    setLoadingMore(true);
    try {
      const oldest = items[items.length - 1]?.created_at;
      const { data } = await api.get(
        `/me/mentions?limit=50&before=${encodeURIComponent(oldest)}`
      );
      setItems((cur) => [...cur, ...(data.mentions || [])]);
      setHasMore(!!data.has_more);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8" data-testid="mentions-page">
        <div className="flex items-center gap-2 mb-2">
          <AtSign className="w-5 h-5 text-hotpink" />
          <div className="font-mono text-hotpink text-xs tracking-widest">// MENTIONS</div>
        </div>
        <h1 className="font-heading text-4xl md:text-6xl leading-none">
          WHERE <span className="text-hotpink">YOU</span> GOT CALLED.
        </h1>
        <p className="font-mono text-white/50 text-xs mt-3">
          // every time someone @{user?.username || "you"} in a room, it lands here
        </p>

        {loading ? (
          <div className="mt-10 font-mono text-white/40 text-sm">// loading…</div>
        ) : items.length === 0 ? (
          <div className="mt-10 border-2 border-white/10 bg-panel/40 p-6 font-mono text-sm text-white/40" data-testid="mentions-empty">
            // no mentions yet · when someone types @{user?.username} in a room, it lands here
          </div>
        ) : (
          <div className="mt-10 border-t border-white/10">
            {items.map((m) => (
              <Link
                key={m.id}
                to={`/room/${m.room_slug}#msg-${m.msg_id}`}
                data-testid={`mention-row-${m.id}`}
                className="group flex items-start gap-4 p-4 border-b border-white/10 hover:bg-panel"
              >
                {m.from_avatar && (
                  <img
                    src={m.from_avatar}
                    alt=""
                    className="w-10 h-10 object-cover border border-white/10 group-hover:border-hotpink shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-mono text-sm text-white">@{m.from_username}</span>
                    <span className="font-mono text-[10px] text-white/40">
                      in
                    </span>
                    <span
                      className="font-mono text-[11px] font-bold tracking-widest uppercase"
                      style={{ color: m.room_color || "#D4FF00" }}
                    >
                      #{m.room_name}
                    </span>
                    <span className="font-mono text-[10px] text-white/30 ml-auto">
                      {new Date(m.created_at).toLocaleString([], {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <div className="font-body text-sm text-white/85 mt-1 break-words">
                    <MentionText
                      body={m.body_preview}
                      mentions={[{ username: user?.username }]}
                      myUsername={user?.username}
                    />
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-hotpink self-center shrink-0" />
              </Link>
            ))}
            {hasMore && (
              <div className="flex justify-center py-4">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="mentions-load-more"
                  className="font-mono text-[10px] px-4 py-2 border-2 border-white/20 text-white/70 hover:border-hotpink hover:text-hotpink uppercase tracking-widest disabled:opacity-50"
                >
                  {loadingMore ? "LOADING…" : "Load more"}
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
